import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDate } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Performance({ user, onLogout }) {
  const [reviews, setReviews] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedReview, setSelectedReview] = useState(null);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [formData, setFormData] = useState({
    employee_id: '',
    review_cycle: '15-Day',
    review_period_start: '',
    review_period_end: '',
    quality_of_work: 3,
    productivity: 3,
    communication: 3,
    teamwork: 3,
    punctuality: 3,
    initiative: 3,
    strengths: '',
    areas_for_improvement: '',
    goals_for_next_period: '',
    reviewer_comments: ''
  });

  useEffect(() => {
    fetchReviews();
    fetchDashboardStats();
    if (user.role === 'Admin' || user.role === 'HR Manager') {
      fetchEmployees();
    }
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
    }
  };

  const fetchReviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/performance-reviews`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReviews(response.data);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      
      if (selectedReview) {
        // Update existing review
        await axios.put(`${API}/performance-reviews/${selectedReview.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Review updated successfully!');
      } else {
        // Create new review
        await axios.post(`${API}/performance-reviews`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Review created successfully!');
      }
      
      setShowForm(false);
      setSelectedReview(null);
      resetForm();
      fetchReviews();
    } catch (error) {
      alert('Failed to save review: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleSubmitReview = async (reviewId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/performance-reviews/${reviewId}/submit`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Review submitted successfully!');
      fetchReviews();
    } catch (error) {
      alert('Failed to submit review: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleAcknowledge = async (reviewId) => {
    const comments = prompt('Add your comments (optional):');
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/performance-reviews/${reviewId}/acknowledge`, 
        { employee_comments: comments },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('Review acknowledged successfully!');
      fetchReviews();
    } catch (error) {
      alert('Failed to acknowledge review: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (review) => {
    setSelectedReview(review);
    setFormData({
      employee_id: review.employee_id,
      review_cycle: review.review_cycle,
      review_period_start: review.review_period_start,
      review_period_end: review.review_period_end,
      quality_of_work: review.quality_of_work,
      productivity: review.productivity,
      communication: review.communication,
      teamwork: review.teamwork,
      punctuality: review.punctuality,
      initiative: review.initiative,
      strengths: review.strengths || '',
      areas_for_improvement: review.areas_for_improvement || '',
      goals_for_next_period: review.goals_for_next_period || '',
      reviewer_comments: review.reviewer_comments || ''
    });
    setShowForm(true);
  };

  const handleDelete = async (reviewId) => {
    if (!window.confirm('Are you sure you want to delete this review?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/performance-reviews/${reviewId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Review deleted successfully!');
      fetchReviews();
    } catch (error) {
      alert('Failed to delete review: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: '',
      review_cycle: '15-Day',
      review_period_start: '',
      review_period_end: '',
      quality_of_work: 3,
      productivity: 3,
      communication: 3,
      teamwork: 3,
      punctuality: 3,
      initiative: 3,
      strengths: '',
      areas_for_improvement: '',
      goals_for_next_period: '',
      reviewer_comments: ''
    });
  };

  const getRatingColor = (rating) => {
    if (rating >= 4.5) return 'text-green-600';
    if (rating >= 3.5) return 'text-[#2b3380]';
    if (rating >= 2.5) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getStatusBadge = (status) => {
    const styles = {
      'Draft': 'bg-gray-100 text-gray-700',
      'Submitted': 'bg-[#2b338030] text-blue-700',
      'Acknowledged': 'bg-green-100 text-green-700'
    };
    return styles[status] || 'bg-gray-100 text-gray-700';
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Performance Reviews</h1>
          {(user.role === 'Admin' || user.role === 'HR Manager') && (
            <button
              onClick={() => { setShowForm(true); setSelectedReview(null); resetForm(); }}
              className="px-6 py-2 rounded-lg text-white hover:opacity-90"
              style={{backgroundColor: '#2b3380'}}
            >
              Create Review
            </button>
          )}
        </div>

        {/* Performance Reviews Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Performance Overview"
            icon="⭐"
            bgColor="#e91e63"
            stats={[
              {
                label: 'Pending Reviews',
                value: dashboardStats.pending_reviews || 0,
                subtitle: 'Draft status',
                color: '#ff9800'
              },
              {
                label: 'Completed Reviews',
                value: dashboardStats.completed_reviews || 0,
                subtitle: 'Submitted',
                color: '#4caf50'
              },
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'To be reviewed',
                color: '#2b3380'
              },
              {
                label: 'Review Cycles',
                value: '2',
                subtitle: '15-Day & Monthly',
                color: '#607d8b'
              }
            ]}
          />
        )}

        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {selectedReview ? 'Edit Review' : 'Create Performance Review'}
                  </h2>
                  <button
                    onClick={() => { setShowForm(false); setSelectedReview(null); }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Employee</label>
                      <select
                        value={formData.employee_id}
                        onChange={(e) => setFormData({...formData, employee_id: e.target.value})}
                        required
                        className="w-full px-3 py-2 border rounded-lg"
                        disabled={selectedReview}
                      >
                        <option value="">Select Employee</option>
                        {employees.map(emp => (
                          <option key={emp.id} value={emp.id}>
                            {emp.first_name} {emp.last_name} ({emp.id})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Review Cycle</label>
                      <select
                        value={formData.review_cycle}
                        onChange={(e) => setFormData({...formData, review_cycle: e.target.value})}
                        required
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="15-Day">15-Day</option>
                        <option value="Monthly">Monthly</option>
                        <option value="Quarterly">Quarterly</option>
                        <option value="Annual">Annual</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Period Start</label>
                      <input
                        type="date"
                        value={formData.review_period_start}
                        onChange={(e) => setFormData({...formData, review_period_start: e.target.value})}
                        required
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Period End</label>
                      <input
                        type="date"
                        value={formData.review_period_end}
                        onChange={(e) => setFormData({...formData, review_period_end: e.target.value})}
                        required
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3" style={{color: '#2b3380'}}>Performance Ratings (1-5)</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { key: 'quality_of_work', label: 'Quality of Work' },
                        { key: 'productivity', label: 'Productivity' },
                        { key: 'communication', label: 'Communication' },
                        { key: 'teamwork', label: 'Teamwork' },
                        { key: 'punctuality', label: 'Punctuality' },
                        { key: 'initiative', label: 'Initiative' }
                      ].map(({ key, label }) => (
                        <div key={key}>
                          <label className="block text-sm font-medium mb-1">{label}</label>
                          <input
                            type="number"
                            min="1"
                            max="5"
                            value={formData[key]}
                            onChange={(e) => setFormData({...formData, [key]: parseInt(e.target.value)})}
                            required
                            className="w-full px-3 py-2 border rounded-lg"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Strengths</label>
                    <textarea
                      value={formData.strengths}
                      onChange={(e) => setFormData({...formData, strengths: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="List key strengths and achievements..."
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Areas for Improvement</label>
                    <textarea
                      value={formData.areas_for_improvement}
                      onChange={(e) => setFormData({...formData, areas_for_improvement: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Identify areas that need development..."
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Goals for Next Period</label>
                    <textarea
                      value={formData.goals_for_next_period}
                      onChange={(e) => setFormData({...formData, goals_for_next_period: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Set goals and expectations..."
                    ></textarea>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Reviewer Comments</label>
                    <textarea
                      value={formData.reviewer_comments}
                      onChange={(e) => setFormData({...formData, reviewer_comments: e.target.value})}
                      rows="3"
                      className="w-full px-3 py-2 border rounded-lg"
                      placeholder="Additional comments..."
                    ></textarea>
                  </div>

                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => { setShowForm(false); setSelectedReview(null); }}
                      className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-lg text-white"
                      style={{backgroundColor: '#2b3380'}}
                    >
                      {selectedReview ? 'Update Review' : 'Create Review'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {reviews.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <p className="text-lg">No performance reviews found.</p>
              {(user.role === 'Admin' || user.role === 'HR Manager') && (
                <p className="mt-2">Create your first review to get started.</p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead style={{backgroundColor: '#2b3380', color: 'white'}}>
                  <tr>
                    <th className="px-4 py-3 text-left">Employee</th>
                    <th className="px-4 py-3 text-left">Cycle</th>
                    <th className="px-4 py-3 text-left">Period</th>
                    <th className="px-4 py-3 text-left">Overall Rating</th>
                    <th className="px-4 py-3 text-left">Reviewer</th>
                    <th className="px-4 py-3 text-left">Status</th>
                    <th className="px-4 py-3 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reviews.map((review) => (
                    <tr key={review.id} className="border-t hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-medium">{review.employee_name}</div>
                        <div className="text-sm text-gray-500">{review.employee_id}</div>
                      </td>
                      <td className="px-4 py-3">{review.review_cycle}</td>
                      <td className="px-4 py-3 text-sm">
                        {formatDate(review.review_period_start)} - {formatDate(review.review_period_end)}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-2xl font-bold ${getRatingColor(review.overall_rating)}`}>
                          {review.overall_rating}
                        </span>
                        <span className="text-gray-500 text-sm">/5</span>
                      </td>
                      <td className="px-4 py-3 text-sm">{review.reviewer_name}</td>
                      <td className="px-4 py-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(review.status)}`}>
                          {review.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => setSelectedReview(review)}
                            className="text-[#2b3380] hover:text-[#2b3380] text-sm font-medium"
                          >
                            View
                          </button>
                          {(user.role === 'Admin' || user.role === 'HR Manager') && (
                            <>
                              {review.status === 'Draft' && (
                                <>
                                  <button
                                    onClick={() => handleEdit(review)}
                                    className="text-green-600 hover:text-green-800 text-sm font-medium"
                                  >
                                    Edit
                                  </button>
                                  <button
                                    onClick={() => handleSubmitReview(review.id)}
                                    className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                                  >
                                    Submit
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleDelete(review.id)}
                                className="text-red-600 hover:text-red-800 text-sm font-medium"
                              >
                                Delete
                              </button>
                            </>
                          )}
                          {user.role === 'Employee' && review.status === 'Submitted' && (
                            <button
                              onClick={() => handleAcknowledge(review.id)}
                              className="text-green-600 hover:text-green-800 text-sm font-medium"
                            >
                              Acknowledge
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {selectedReview && !showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>Performance Review Details</h2>
                  <button
                    onClick={() => setSelectedReview(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-gray-600">Employee:</p>
                      <p className="font-semibold">{selectedReview.employee_name}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Review Cycle:</p>
                      <p className="font-semibold">{selectedReview.review_cycle}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Review Period:</p>
                      <p className="font-semibold">
                        {formatDate(selectedReview.review_period_start)} - {formatDate(selectedReview.review_period_end)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-600">Reviewer:</p>
                      <p className="font-semibold">{selectedReview.reviewer_name}</p>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h3 className="font-semibold mb-3" style={{color: '#2b3380'}}>Performance Ratings</h3>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {[
                        { key: 'quality_of_work', label: 'Quality of Work' },
                        { key: 'productivity', label: 'Productivity' },
                        { key: 'communication', label: 'Communication' },
                        { key: 'teamwork', label: 'Teamwork' },
                        { key: 'punctuality', label: 'Punctuality' },
                        { key: 'initiative', label: 'Initiative' }
                      ].map(({ key, label }) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-gray-600">{label}:</span>
                          <span className="font-semibold">{selectedReview[key]}/5</span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-3 pt-3 border-t flex justify-between items-center">
                      <span className="font-semibold">Overall Rating:</span>
                      <span className={`text-2xl font-bold ${getRatingColor(selectedReview.overall_rating)}`}>
                        {selectedReview.overall_rating}/5
                      </span>
                    </div>
                  </div>

                  {selectedReview.strengths && (
                    <div>
                      <h4 className="font-semibold mb-2" style={{color: '#2b3380'}}>Strengths</h4>
                      <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{selectedReview.strengths}</p>
                    </div>
                  )}

                  {selectedReview.areas_for_improvement && (
                    <div>
                      <h4 className="font-semibold mb-2" style={{color: '#2b3380'}}>Areas for Improvement</h4>
                      <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{selectedReview.areas_for_improvement}</p>
                    </div>
                  )}

                  {selectedReview.goals_for_next_period && (
                    <div>
                      <h4 className="font-semibold mb-2" style={{color: '#2b3380'}}>Goals for Next Period</h4>
                      <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{selectedReview.goals_for_next_period}</p>
                    </div>
                  )}

                  {selectedReview.reviewer_comments && (
                    <div>
                      <h4 className="font-semibold mb-2" style={{color: '#2b3380'}}>Reviewer Comments</h4>
                      <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded">{selectedReview.reviewer_comments}</p>
                    </div>
                  )}

                  {selectedReview.employee_comments && (
                    <div>
                      <h4 className="font-semibold mb-2" style={{color: '#2b3380'}}>Employee Comments</h4>
                      <p className="text-sm text-gray-700 bg-green-50 p-3 rounded">{selectedReview.employee_comments}</p>
                    </div>
                  )}

                  <div className="text-xs text-gray-500 text-center pt-4 border-t">
                    <p>Created: {formatDate(selectedReview.created_at)}</p>
                    {selectedReview.submitted_at && <p>Submitted: {formatDate(selectedReview.submitted_at)}</p>}
                    {selectedReview.acknowledged_at && <p>Acknowledged: {formatDate(selectedReview.acknowledged_at)}</p>}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}

export default Performance;
